import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../context/AuthContext";
import { initNativePush, setPushNavigator, isNativeSync } from "./pushNative";

// Amorçage des fonctionnalités NATIVES (app Android Capacitor uniquement).
// No-op total sur le web. Branche :
//   - le routeur pour les deep links de notifications (clic → bon écran) ;
//   - l'init du push natif FCM (canaux + permission + token) quand un
//     utilisateur est connecté.
// Le reste de l'app (UX mobile : back button, status bar…) est géré ici aussi
// pour rester centralisé.
export default function NativeBootstrap() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Donne au module push (hors React) le moyen de naviguer.
  useEffect(() => { setPushNavigator(navigate); }, [navigate]);

  // IN-APP UPDATE Play (demande Lucas 2026-07-12) : ~5 s après le boot natif
  // (jamais en concurrence avec l'OTA ni le premier rendu), propose la mise à
  // jour FLEXIBLE (bottom-sheet Play, téléchargement en arrière-plan) — ou
  // IMMEDIATE si le binaire ne peut plus recevoir l'OTA (contrat) / priorité
  // Play haute. Fail-open : binaire sans plugin = no-op. Décision pure +
  // throttle 24 h : src/native/appUpdate/appUpdateDecision.js.
  useEffect(() => {
    if (!isNativeSync()) return undefined;
    const t = setTimeout(() => {
      import("./appUpdate/appUpdateRuntime")
        .then((m) => m.maybeProposeAppUpdate())
        .catch(() => { /* fail-open */ });
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  // Init push FCM dès qu'un utilisateur est connecté (idempotent).
  useEffect(() => {
    if (user) initNativePush(api);
  }, [user]);

  // DEEP LINKS / App Links (release v21) : un lien https://www.gively.fr/…
  // (email, notif, partage) ouvre l'app via l'intent-filter autoVerify injecté
  // par prepare-android.sh — ce listener route alors vers la BONNE page de la
  // SPA. Sans lui, l'app s'ouvrait sur l'accueil et l'utilisateur perdait sa
  // destination (annonce, conversation, versement…).
  useEffect(() => {
    if (!isNativeSync()) return undefined;
    let remove = null;
    // Route une URL gively.fr vers la SPA (partagé listener + cold start).
    const routeGivelyUrl = (url) => {
      try {
        const u = new URL(url);
        if (u.hostname === "www.gively.fr" || u.hostname === "gively.fr") {
          navigate(u.pathname + u.search + u.hash);
        }
      } catch (_) { /* URL invalide → ignorer */ }
    };
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        // App CHAUDE/en fond : événement appUrlOpen.
        const sub = await App.addListener("appUrlOpen", ({ url }) => routeGivelyUrl(url));
        remove = () => sub.remove();
        // COLD START (demande Lucas, RC v21) : quand l'app était complètement
        // FERMÉE et qu'on l'ouvre via un lien, appUrlOpen n'est pas rejoué —
        // l'URL de lancement se lit via App.getLaunchUrl() (API officielle
        // @capacitor/app). Comportement attendu type Airbnb/Vinted : le lien
        // amène DIRECTEMENT sur la bonne page, pas sur l'accueil. Garde
        // anti-double-navigation : une seule consommation par démarrage.
        if (!window.__givelyLaunchUrlConsumed) {
          window.__givelyLaunchUrlConsumed = true;
          const launch = await App.getLaunchUrl();
          if (launch && launch.url) routeGivelyUrl(launch.url);
        }
      } catch (e) { /* plugin absent / web */ }
    })();
    return () => { if (remove) remove(); };
  }, [navigate]);

  // Bouton RETOUR Android : sur les écrans racine, on laisse l'OS gérer
  // (minimise l'app) ; sinon on revient en arrière dans l'historique React.
  useEffect(() => {
    if (!isNativeSync()) return undefined;
    let remove = null;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const sub = await App.addListener("backButton", ({ canGoBack }) => {
          if (canGoBack && window.history.length > 1) {
            window.history.back();
          } else {
            App.exitApp();
          }
        });
        remove = () => sub.remove();
      } catch (e) { /* plugin absent / web */ }
    })();
    return () => { if (remove) remove(); };
  }, []);

  // Splash screen : on le masque dès que React a monté (évite le double-splash
  // Android 12+ et donne une ouverture nette/premium au lieu d'un délai figé).
  useEffect(() => {
    if (!isNativeSync()) return;
    (async () => {
      try {
        const { SplashScreen } = await import("@capacitor/splash-screen");
        await SplashScreen.hide();
      } catch (e) { /* plugin absent */ }
    })();
  }, []);

  // Barre de statut Android : le CONTENU ne doit PAS passer sous l'heure/batterie.
  // Android 15/16 impose l'edge-to-edge ; `setOverlaysWebView({overlay:false})`
  // redimensionne la WebView sous la barre. On garde fond blanc + icônes sombres.
  // Le CSS safe-area (env(safe-area-inset-top) dans index.css) sert de filet si
  // l'OS force malgré tout l'edge-to-edge.
  useEffect(() => {
    if (!isNativeSync()) return;
    (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        try { await StatusBar.setOverlaysWebView({ overlay: false }); } catch (_) { /* selon version */ }
        await StatusBar.setStyle({ style: Style.Light }); // texte sombre sur fond clair
        try { await StatusBar.setBackgroundColor({ color: "#ffffff" }); } catch (_) { /* ignoré en edge-to-edge */ }
      } catch (e) { /* noop */ }
    })();
  }, []);

  // MISE À JOUR OTA (Over-The-Air) : au démarrage natif, on vérifie s'il existe un
  // nouveau bundle web SIGNÉ et COMPATIBLE avec ce binaire (même NATIVE_CONTRACT),
  // on le télécharge (checksum vérifié nativement) et on l'installe pour le PROCHAIN
  // démarrage. Fail-safe total : toute erreur laisse l'app sur son bundle courant.
  // markReady() confirme d'abord que le bundle actif démarre bien (désarme le rollback
  // auto natif). No-op sur le web / si le plugin est absent.
  useEffect(() => {
    if (!isNativeSync()) return;
    (async () => {
      const { otaLog, otaReport } = await import("./ota/otaLog");
      try {
        otaLog("start", { native: true });
        const { createCapawesomeProvider } = await import("./ota/otaCapawesomeProvider");
        const provider = await createCapawesomeProvider();
        if (!provider) { otaReport("skip", { reason: "plugin absent (build sans OTA)" }); return; }
        await provider.markReady(); // bundle courant sain → pas de rollback
        const { checkForUpdate, manifestUrlFor } = await import("./ota/otaRuntime");
        const { NATIVE_CONTRACT_VERSION, OTA_BASE_URL, EMBEDDED_BUNDLE_ID } = await import("./ota/otaConfig");
        const currentBundleId = await provider.currentBundleId();
        otaLog("context", { nativeContract: NATIVE_CONTRACT_VERSION, embeddedBundleId: EMBEDDED_BUNDLE_ID, currentBundleId, baseUrl: OTA_BASE_URL });
        const res = await checkForUpdate({
          manifestUrl: manifestUrlFor({ baseUrl: OTA_BASE_URL, nativeContract: NATIVE_CONTRACT_VERSION }),
          app: { nativeContract: NATIVE_CONTRACT_VERSION, currentBundleId },
          provider,
        });
        // res.action ∈ applied | skip | reject. 'applied' = installé pour le prochain démarrage.
        otaReport(res && res.action === "applied" ? "applied" : (res && res.action) || "skip", {
          action: res && res.action, reason: res && res.reason, bundleId: res && res.bundleId,
        });
      } catch (e) {
        // OTA best-effort, jamais bloquant — mais on REMONTE l'erreur (observabilité).
        otaReport("failed", { error: e && e.message });
      }
    })();
  }, []);

  return null;
}
